import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { getPassengerLabel, isEmptyArray, isEmptyObject, isStringEmpty, scrollToElement } from '@common/helpers';
import {
  FareConditionsBaggagePoliciesBaggageAllowance,
  FareConditionsCheckedBaggageAllowanceBaggageBoundsInner,
  FareConditionsResponseDataBaggagePoliciesBaggageAllowanceBaggageBoundsInnerCheckedBaggageAllowanceDetailsInner,
} from '@common/interfaces/mybooking-itinerary/mybooking-itinerary.interface';
import { FirstBaggagePnr } from '@common/interfaces/mybooking/order-baggage.interface';
import { ServicingCommonStoreService } from '@common/services';
import { GetFareConditionsStoreService } from '@common/services/my-booking/get-fare-conditions/get-fare-conditions.service';
import { MyBookingItineraryService } from '@common/services/my-booking/my-booking-itinerary.service';
import { SupportComponent } from '@lib/components/support-class';
import { AmountFormatPipe, StaticMsgPipe } from '@lib/pipes';
import { CommonLibService } from '@lib/services';
import { Subscription, combineLatestWith, debounceTime } from 'rxjs';
import {
  FareConditionsResponseData,
  FareConditionsResponseDataBaggagePolicies,
  GetOrderResponseData,
  GetOrderResponseDataAir,
  Traveler,
} from 'src/sdk-servicing';

@Component({
  selector: 'asw-baggage-rules',
  templateUrl: './baggage-rules.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaggageRulesComponent extends SupportComponent implements AfterViewInit {
  @Input() functionList!: { [key: string]: Function };
  private _subscriptions: Subscription = new Subscription();
  /** はスクロールです */
  public isScrollCheckedBaggage: boolean = false;
  public isScrollBaggageRules: boolean = false;
  public isScrollCarryOnBaggage: boolean = false;

  /** ポリシー規制列挙型 */
  USDOT = FareConditionsResponseDataBaggagePolicies.PolicyRegulationsEnum.Usdot;
  CADOT = FareConditionsResponseDataBaggagePolicies.PolicyRegulationsEnum.Cadot;
  IATARESO302 = FareConditionsResponseDataBaggagePolicies.PolicyRegulationsEnum.IataReso302;

  /** 運賃条件の回答 */
  fareConditions!: FareConditionsResponseData;
  baggageAllowance!: FareConditionsBaggagePoliciesBaggageAllowance;

  /** 注文応答データの取得 */
  orderData!: GetOrderResponseData;

  isShowBaggageRule: boolean = false;
  checkedBaggage: number = 1;
  carryOnBaggage: number = 2;
  bags = ['label.1stPiece', 'label.2ndPiece'];
  scrollId: string = 'my-booking-itinerary-baggage-rules-scroll';
  CHECKEDBAGGAGE: string = 'checked-baggage';
  CARRYONBAGGAGE: string = 'carry-on-baggage';
  CHECKEDBAGGAGEALLOWANCE: string = 'checkedBaggageAllowance';
  CARRYONALLOWANCE: string = 'carryOnAllowance';

  constructor(
    private _common: CommonLibService,
    private _change: ChangeDetectorRef,
    private _myBookingItineraryService: MyBookingItineraryService,
    private _servicingCommonStoreService: ServicingCommonStoreService,
    private _fareConditionsStoreService: GetFareConditionsStoreService,
    private _staticMsg: StaticMsgPipe,
    public _amountFormatPipe: AmountFormatPipe
  ) {
    super(_common);
  }

  ngAfterViewInit(): void {
    this._subscriptions.add(
      this._myBookingItineraryService.scrollObservable
        .pipe(combineLatestWith(this._fareConditionsStoreService.getFareConditionsObservable()), debounceTime(100))
        .subscribe(([scrollId]) => {
          if (scrollId === this.scrollId) {
            scrollToElement(this.scrollId);
          }
        })
    );
  }

  init(): void {
    this._subscriptions.add(
      this._servicingCommonStoreService.getServicingCommonModel$().subscribe((servicingCommon) => {
        this.orderData = servicingCommon?.getOrder ?? ({} as GetOrderResponseData);
        this._change.markForCheck();
      })
    );
    this._subscriptions.add(
      this._fareConditionsStoreService.getFareConditionsObservable().subscribe((fareCondition) => {
        if (!fareCondition?.isPending && !isEmptyObject(fareCondition?.model?.data)) {
          this.isShowBaggageRule = true;
        } else {
          this.isShowBaggageRule = false;
        }
        this.fareConditions = fareCondition.model?.data ?? ({} as FareConditionsResponseData);
        this.baggageAllowance =
          (this.fareConditions?.baggagePolicies?.baggageAllowance as FareConditionsBaggagePoliciesBaggageAllowance) ??
          ({} as FareConditionsBaggagePoliciesBaggageAllowance);
        this._change.markForCheck();
      })
    );
  }
  reload(): void {}
  destroy(): void {
    if (this._subscriptions) {
      this._subscriptions.unsubscribe();
    }
  }

  /** Check if table slider is scrolling or not with baggage-rules */
  scrollChangeBaggageRules(value: boolean) {
    this.isScrollBaggageRules = value;
  }

  /** Check if table slider is scrolling or not with checked-baggage */
  scrollChangeCheckedBaggage(value: boolean) {
    this.isScrollCheckedBaggage = value;
  }

  /** Check if table slider is scrolling or not with Carry-on baggage */
  scrollChangeCarryOnBaggage(value: boolean) {
    this.isScrollCarryOnBaggage = value;
  }

  get getPolicyRegulations(): FareConditionsResponseDataBaggagePolicies.PolicyRegulationsEnum {
    return this.fareConditions?.baggagePolicies
      ?.policyRegulations as FareConditionsResponseDataBaggagePolicies.PolicyRegulationsEnum;
  }

  /** [1.6.2] 手荷物ルール表示不可案内 */
  get isBaggageRulesDisplayed(): boolean {
    /**
     * 以下のいずれかを満たす場合、"label.canNotdisplayedBaggageRules" (e-Ticketの手荷物ルールの確認を促す文言)を表示する。
     * 1)運賃ルール・手荷物情報取得API応答.data.baggagePoliciesが存在しない
     * 2)運賃ルール・手荷物情報取得API応答.data.baggagePolicies.policyRegulationsが存在し
     */
    return (
      isEmptyObject(this.fareConditions?.baggagePolicies) ||
      isStringEmpty(this.fareConditions?.baggagePolicies?.policyRegulations)
    );
  }

  /** [1.6.11] 手荷物ルール */
  get isShowBaggageRules(): boolean {
    /**
     * 1)運賃ルール・手荷物情報取得API応答.data.baggagePoliciesが存在するの場合、表示する。
     * 2)上記以外の場合、非表示とする。
     */
    return !isEmptyObject(this.fareConditions?.baggagePolicies);
  }

  /** [1.6.14] 出発地 / [1.6.15] 到着地 */
  getPointOfDepartureArrival(locationCode: string, locationName: string) {
    /**
     * 出発地
     * 1) 当該適用区間情報.departure.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
     * 2) 取得できない場合は当該適用区間情報.departure.locationNameを表示する。
     * 到着地
     * 1) 当該適用区間情報.arrival.locationCodeを基に「イベントID(1)」で取得した空港リストをフィルタして、空港名称を取得する。
     * 2) 取得できない場合は当該適用区間情報.arrival.locationNameを表示する。
     */
    return this._myBookingItineraryService.getAirportLocationName(locationCode, locationName);
  }

  /**
   * 下記のデータを元に区間別ルールのグループを表示する。
   * 1)預入手荷物ルールのグループについて、当該適用区間情報.checkedBaggageAllowanceが存在する場合、当該適用区間情報.checkedBaggageAllowance(以下当該手荷物ルール情報とする)を使用して表示する
   */
  isShowCheckedBaggageAllowance(baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner): boolean {
    return !isEmptyObject(baggageBound?.checkedBaggageAllowance);
  }

  /**
   * 下記のデータを元に区間別ルールのグループを表示する。
   * 2)機内持ち込み手荷物ルールのグループについて、当該適用区間情報.carryOnAllowanceが存在する場合、  当該適用区間情報.carryOnAllowance(以下当該手荷物ルール情報とする) を使用して表示する
   */
  isShowCarryOnAllowance(baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner): boolean {
    return !isEmptyObject(baggageBound?.carryOnAllowance);
  }

  /** [1.6.16] ルール適用キャリア名 */
  get isShowCarrierNameByUSDOTOrCADOT(): boolean {
    /**
     * 1)運賃ルール・手荷物情報取得API応答.data.baggagePolicies.policyRegulations="USDOT"または"CADOT"の場合、以下の内容を表示する。
     * 2)上記以外の場合、何も表示しない。
     */
    return this.getPolicyRegulations === this.USDOT || this.getPolicyRegulations === this.CADOT;
  }

  /** [1.6.26]-[1.6.16] ルール適用キャリア名 */
  getCarrierNameByUSDOTOrCADOT(locationCode?: string, locationName?: string) {
    /**
     * 1.1)当該手荷物ルール情報.airlineCodeを基に「イベントID(1)」で取得したキャリア(国際化)リストをフィルタして、キャリア名称を取得する。
     * 1.2) 取得できない場合は当該手荷物ルール情報.airlineNameを表示する。
     */
    return this._myBookingItineraryService.getAirlineFromCarrierList(locationCode, locationName);
  }

  /** [1.6.26]-[1.6.17] ルール詳細コンテンツ */
  isNhGroupOperatingByUSDOTOrCADOT(
    baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner,
    typeBaggage?: string
  ): boolean {
    /**
     * 1)運賃ルール・手荷物情報取得API応答.data.baggagePolicies.policyRegulations="USDOT"または"CADOT"の場合、以下の内容を表示する。
     * 1.1)当該手荷物ルール情報.isNhGroupOperating=trueの場合、NH(国際)ルールのコンテンツへのリンクを表示する。
     * 1.2)上記以外の場合、何も表示しない。
     */
    if (this.getPolicyRegulations === this.USDOT || this.getPolicyRegulations === this.CADOT) {
      if (typeBaggage === this.CHECKEDBAGGAGE) {
        return Boolean(baggageBound?.checkedBaggageAllowance?.isNhGroupOperating) === true;
      } else if (typeBaggage === this.CARRYONBAGGAGE) {
        return Boolean(baggageBound?.carryOnAllowance?.isNhGroupOperating) === true;
      }
    }
    return false;
  }

  /** [1.6.17] ルール詳細コンテンツ */
  get isNhGroupOperatingByIATARESO302(): boolean {
    /** 2)運賃ルール・手荷物情報取得API応答.baggagePolicies.policyRegulations="IATA RESO 302"の場合、旅程種別と当該区間の運航キャリア種別に応じて、以下のいずれかに遷移するリンクを表示する。*/
    return this.getPolicyRegulations === this.IATARESO302;
  }

  /** [1.6.17] ルール詳細コンテンツ  Item 2.2 */
  isNhGroupOperatingByCheckedBaggageOrCarryOn(
    typeBaggage: string,
    baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner
  ): boolean {
    /** 当該適用区間情報.checkedBaggageAllowance.isNhGroupOperating=true(運航キャリア種別がNHグループ) */
    if (typeBaggage === this.CHECKEDBAGGAGE) {
      return Boolean(baggageBound?.checkedBaggageAllowance?.isNhGroupOperating) === true;
    }
    return false;
  }

  /** [1.6.17] ルール詳細コンテンツ */
  getIsNhGroupOperatingByIATARESO302(
    typeBaggage: string,
    baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner
  ): string {
    /** 2.1)PNR情報取得API応答.data.air.tripType="domestic"(日本国内単独旅程)の場合、日本国内単独旅程ルール詳細のコンテンツへのリンクを表示する。*/
    if (this.orderData?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.Domestic) {
      return 'message.baggageAllowance.details';
    }

    /** 2.2)PNR情報取得API応答.data.air.tripType="international"(国際旅程)、かつ当該適用区間情報.checkedBaggageAllowance.isNhGroupOperating=true(運航キャリア種別がNHグループ)の場合、NH(国際)ルール詳細のコンテンツへのリンクを表示する。*/
    if (
      this.orderData?.air?.tripType === GetOrderResponseDataAir.TripTypeEnum.International &&
      this.isNhGroupOperatingByCheckedBaggageOrCarryOn(typeBaggage, baggageBound)
    ) {
      return 'message.baggageAllowanceNh.details';
    }
    /** 2.3)上記以外の場合、その他他社向けの手荷物ルール詳細のコンテンツへのリンクを表示する。*/
    return 'message.callCenter';
  }

  /** [1.6.26]-[1.6.27] 事前追加手荷物 */
  get isShowPrePaidExtraBaggage(): boolean {
    /**
     * 1)預入手荷物ルールのグループの場合、
     * 1.1)運賃ルール・手荷物情報取得API応答.baggagePolicies.policyRegulations="IATA RESO 302"の場合、事前追加手荷物の申込リンク、申込状況を表示する。
     * 1.2)上記以外の場合、何も表示しない。
     * 2)機内持ち込み手荷物ルールのグループの場合、何も表示しない。
     */
    return this.getPolicyRegulations === this.IATARESO302;
  }

  /** [1.6.28] 手荷物申込モーダル表示 */
  getBoundId(baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner): string {
    /** ※PNR情報.data..air.bounds[].flights[]の内に、id=当該手荷物ルール情報.flightIds[0]である要素が存在する場合、PNR情報.data..air.bounds[].airBoundIdを当該バウンドIDとする。*/
    let airBoundId: string = '';
    this.orderData?.air?.bounds?.forEach((bound) => {
      const foundFlightId = bound?.flights?.every(
        (flight) => String(flight?.id) === String(baggageBound?.checkedBaggageAllowance?.flightIds?.[0])
      );
      if (foundFlightId) {
        airBoundId = bound?.airBoundId ?? '';
      }
    });
    return airBoundId;
  }

  /** [1.6.28] 手荷物申込モーダル表示 */
  isShowBaggageModal(baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner): boolean {
    /**
     * 1)PNR情報.data.services.baggage.firstBaggage.<当該手荷物ルール情報.flightIds[0]>.isAvailable=trueの場合、事前追加手荷物が申込可能な旨、および手荷物申込画面(S02-M016)を開くためのリンクを表示する。
     * 2)上記以外の場合、手荷物申込不可の旨を表示する。
     */
    const firstBaggage =
      (this.orderData?.services?.baggage?.firstBaggage as FirstBaggagePnr) ?? ({} as FirstBaggagePnr);
    return Boolean(firstBaggage[this.getBoundId(baggageBound)]?.isAvailable) === true;
  }

  /** [1.6.28] 手荷物申込モーダル表示 */
  openS02M016Modal() {
    this.functionList['clickBaggageApplication']();
  }

  /** [1.6.29] 無料預入手荷物0個時の案内 */
  infoFreeCheckedBaggageAllowanceIsZeroByIATARESO302(
    baggageBound?: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner
  ): boolean {
    /**
     * 1)預入手荷物ルールのグループの場合、以下の内容を表示する。
     * 1.1)運賃ルール・手荷物情報取得API応答.baggagePolicies.policyRegulations="IATA RESO 302"、かつ当該手荷物ルール情報.quantityが存在する、かつ当該手荷物ルール情報.quantity="0"の場合、無料預入手荷物0個時の案内の旨を表示する。
     * 1.2)上記以外の場合、何も表示しない。
     * 2)機内持ち込み手荷物ルールのグループの場合、何も表示しない。
     * ※案内は旅程が日本国内単独旅程かどうかに応じて別々の案内とする。
     */
    return this.getPolicyRegulations === this.IATARESO302 && baggageBound?.checkedBaggageAllowance?.quantity === 0;
  }

  /** [1.6.11] 手荷物ルール */
  getBaggageBoundsByDefault(): Array<FareConditionsCheckedBaggageAllowanceBaggageBoundsInner> {
    /**
     * PNR情報取得API応答.data.travelersのtravelerIdを取得して搭乗者IDリストを作成する。
     * <以下、運賃ルール・手荷物情報取得API応答.data.baggagePolicies.baggageAllowance.<搭乗者IDリスト[0]>.baggageBounds[]の要素数繰り返し>(それぞれの要素が当該預入手荷物ルール情報)
     * <ここまで、運賃ルール・手荷物情報取得API応答.data.baggagePolicies.baggageAllowance.<搭乗者IDリスト[0]>.baggageBounds[]の要素数繰り返し>
     */
    if (this.orderData && this.fareConditions) {
      let id = this.orderData?.travelers?.map((m) => m.id)?.[0];
      if (id) {
        if (this.baggageAllowance?.[id]?.baggageBounds) {
          return (
            this.baggageAllowance?.[id]?.baggageBounds ??
            ([] as Array<FareConditionsCheckedBaggageAllowanceBaggageBoundsInner>)
          );
        }
      }
    }
    return [] as Array<FareConditionsCheckedBaggageAllowanceBaggageBoundsInner>;
  }

  /** 運賃ルール・手荷物情報取得API応答.data.baggagePolicies.baggageAllowance.<当該搭乗者情報.id>.baggageBounds[]の要素数繰り返し>(それぞれの要素が当該) */
  getBaggageBoundsByPassengerId(passengerId?: string): FareConditionsCheckedBaggageAllowanceBaggageBoundsInner[] {
    if (this.fareConditions && passengerId) {
      if (this.baggageAllowance?.[passengerId]?.baggageBounds) {
        return (
          this.baggageAllowance?.[passengerId]?.baggageBounds ??
          ([] as Array<FareConditionsCheckedBaggageAllowanceBaggageBoundsInner>)
        );
      }
    }
    return [] as Array<FareConditionsCheckedBaggageAllowanceBaggageBoundsInner>;
  }

  /** [1.6.26]-[1.6.19] 搭乗者姓名 */
  showTravelerName(traveler?: Traveler) {
    return traveler?.names?.[0];
  }

  /** [1.6.26]-[1.6.20] 搭乗者種別 */
  passengerLabel(code?: string) {
    return getPassengerLabel(String(code));
  }

  /** [1.6.22] 無料手荷物個数 */
  quantityCheckedBaggageAllowance(baggageBound: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner): number {
    /**
     * 当該区間の無料手荷物個数に応じ、以下の内容を表示する。
     * 1)当該手荷物ルール情報.quantity="0"の場合、および個数を含む文言手荷物を預けられない旨のアイコン、および個数を含む文言。
     * 2)当該手荷物ルール情報.quantity≥"1"の場合、および個数を含む文言手荷物を預けられる旨のアイコン、および個数を含む文言。
     */
    return baggageBound?.checkedBaggageAllowance?.quantity ?? 0;
  }

  /** [1.6.26]-[1.6.22] 無料手荷物個数 */
  quantityCheckedCarryOnAllowance(baggageBound: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner): number {
    /**
     * 当該区間の無料手荷物個数に応じ、以下の内容を表示する。
     * 1)当該手荷物ルール情報.quantity="0"の場合、および個数を含む文言手荷物を預けられない旨のアイコン、および個数を含む文言。
     * 2)当該手荷物ルール情報.quantity≥"1"の場合、および個数を含む文言手荷物を預けられる旨のアイコン、および個数を含む文言。
     */
    return baggageBound?.carryOnAllowance?.quantity ?? 0;
  }

  /** [1.6.22] 無料手荷物個数 */
  getAllowanceQuantity(baggageBound: FareConditionsCheckedBaggageAllowanceBaggageBoundsInner, type: number): string {
    let quantity;
    let label: string = '';
    const isChecked = type === this.checkedBaggage;
    const isCarryOn = type === this.carryOnBaggage;
    if (isChecked || isCarryOn) {
      const allowance = isChecked ? baggageBound?.checkedBaggageAllowance : baggageBound?.carryOnAllowance;
      if (allowance?.quantity != null && Number(allowance.quantity) >= 0) {
        quantity = Number(allowance.quantity);
      }
      label = quantity !== undefined ? (isChecked ? 'label.checkedBaggage1' : 'label.carry-onBaggage') : '';
    }

    return (label && this._staticMsg.transform(label, { '0': quantity })) || label;
  }

  /** [1.6.23] ルール詳細 */
  get isShowDetailsByUSDOTOrCADOT(): boolean {
    /**
     * [1.6.23.1] 運賃ルール・手荷物情報取得API応答.baggagePolicies.policyRegulations="IATA RESO 302"の場合、運賃ルール・手荷物情報取得APIから許容量詳細情報は返却されないため、表示しない。
     */
    return this.getPolicyRegulations === this.IATARESO302;
  }

  /** [1.6.23.2] 上記以外の場合、かつ当該手荷物ルール情報.details[]が存在する場合、表示する。 */
  isEmptyAllowanceDetails(bagIdx: number, travelerID?: string, type: string = this.CHECKEDBAGGAGEALLOWANCE): boolean {
    if (!this.fareConditions || !this.baggageAllowance || !travelerID) {
      return false;
    }

    const baggageBounds = this.baggageAllowance?.[travelerID]?.baggageBounds ?? [];

    if (type === this.CHECKEDBAGGAGEALLOWANCE) {
      return baggageBounds?.every((item) => isEmptyObject(item.checkedBaggageAllowance?.details?.[bagIdx]));
    }

    return baggageBounds?.every((item) => isEmptyObject(item.carryOnAllowance?.details?.[bagIdx]));
  }

  /** 手荷物個数分について、以下の内容を表示する。(2) */
  /** 全個目を表示するには、当該手荷物ルール情報.details[]繰り返し表示する。*/
  /** [1.6.26]-[1.6.24] 料金 */
  getFeeDetail(
    details: Array<FareConditionsResponseDataBaggagePoliciesBaggageAllowanceBaggageBoundsInnerCheckedBaggageAllowanceDetailsInner>,
    bag: number
  ) {
    let result: string = '';
    let isFree = details?.[bag]?.isFree;
    if (isFree === true) {
      result = this._staticMsg.transform('label.free');
    } else if (isFree === false) {
      result = this.getFee(details?.[bag]);
    }
    return result;
  }

  /** [1.6.26]-[1.6.24] 料金 */
  getFee(
    detail?: FareConditionsResponseDataBaggagePoliciesBaggageAllowanceBaggageBoundsInnerCheckedBaggageAllowanceDetailsInner
  ): string {
    let result: string = '';
    if (!isEmptyObject(detail?.totalPrices)) {
      result = this._amountFormatPipe.transform(
        detail?.totalPrices?.price ?? 0,
        undefined,
        detail?.totalPrices?.currencyCode ?? ''
      );
    }
    return result;
  }

  /** [1.6.26]-[1.6.25] フリーテキスト */
  getDescriptionDetail(
    details: Array<FareConditionsResponseDataBaggagePoliciesBaggageAllowanceBaggageBoundsInnerCheckedBaggageAllowanceDetailsInner>,
    bag: number,
    flag: string
  ) {
    return this.getDescription(details?.[bag], flag);
  }

  /** [1.6.26]-[1.6.25] フリーテキスト */
  getDescription(
    detail?: FareConditionsResponseDataBaggagePoliciesBaggageAllowanceBaggageBoundsInnerCheckedBaggageAllowanceDetailsInner,
    flag?: string
  ) {
    let result: string = '';
    if (!isEmptyObject(detail)) {
      /** 1)当該手荷物個数分.policyDetails.weightが存在する場合 */
      const weight = Number(detail?.policyDetails?.weight);
      if (weight) {
        if (flag === this.CHECKEDBAGGAGE) {
          /**
           * 1.1)預入手荷物ルールの場合、
           * 1.1.1)当該手荷物個数分.policyDetails.weight≤23の場合、手荷物のサイズと23kg重量に関する文言を表示する。
           * 1.1.2)23<当該手荷物個数分.policyDetails.weight≤32の場合、手荷物のサイズと32kg重量に関する文言を表示する。
           */
          if (weight <= 23) {
            result = this._staticMsg.transform('message.freeBaggageAllowanceWeight.23kg');
          } else if (23 < weight && weight <= 32) {
            result = this._staticMsg.transform('message.freeBaggageAllowanceWeight.32kg');
          }
        } else if (flag === this.CARRYONBAGGAGE && weight <= 10) {
          /**
           * 1.2)機内持ち込み手荷物ルールの場合、
           * 1.2.1)当該手荷物個数分.policyDetails.weight≤10の場合、手荷物のサイズと10kg重量に関する文言を表示する。
           */
          result = this._staticMsg.transform('message.freeCarryOnBaggageAllowanceWeight.10kg');
        }
      } else if (!isEmptyArray(detail?.descriptions)) {
        /** 2)当該手荷物個数分.descriptions[]が存在する場合、フリーテキストのリスト件数分、フリーテキストを改行して表示 */
        result = String(detail?.descriptions?.join('\n'));
      }
    }
    return result;
  }
}
